import { countsInHeadcount, fxSalary } from './cost.js';
import { hasOwn } from './domain.js';
/**
 * Every person at or below a chart node, group heads included, taken from the RENDERED tree.
 *
 * Two rules make this correct where a naive walk is not.
 *
 * HEADS ARE NOT LEAVES. `buildHierarchy` deliberately omits somebody who heads an ancestor group from
 * the leaves beneath it — they are drawn on the group's own card instead — so collecting person nodes
 * alone silently skips every manager. A bulk that quietly left a team's head behind, while the group
 * card's headcount (which does count them) said otherwise, is the class of wrong number this port
 * exists to avoid.
 *
 * PEOPLE HAVE CHILDREN. In the grouping layout they do not, so stopping at a person is harmless there.
 * In the REPORTING layout every person's parent is their manager, so stopping at a person collects
 * direct reports only — a manager's selection would silently exclude everybody more than one level
 * below them. Recursing makes one function right in both layouts, and makes the selection agree with
 * the number already printed on the card: `_roll.reports` is the full recursive subtree.
 *
 * The anchor is included when it is a person, and `ROOT_ID` returns everyone in the tree — which is the
 * "select the whole organisation" gesture. Callers cap and disclose; this function only answers.
 *
 * `descendantPeople` in packages/export/src/deck-tree.ts is the same rule and deliberately NOT this
 * function: it walks a stack, so it emits depth-first reversed, and the first three names it yields are
 * printed on a deck box and compared against the frozen app. Same population — parity confirms it — and a
 * different order that is client-visible output. Change either and change both.
 */
export function subtreePeople(nodes, nodeId) {
    const byParent = new Map();
    const byId = new Map();
    for (const node of nodes) {
        const list = byParent.get(node.parentId) ?? [];
        list.push(node);
        byParent.set(node.parentId, list);
        byId.set(node.id, node);
    }
    // Keyed by id, because a group and its parent can both name the same head.
    const out = new Map();
    const add = (person) => {
        if (person)
            out.set(String(person.id), person);
    };
    const anchor = byId.get(nodeId);
    if (!anchor)
        return [];
    add(anchor.kind === 'person' ? anchor.data : anchor.data.head);
    const walk = (id) => {
        for (const child of byParent.get(id) ?? []) {
            add(child.kind === 'person' ? child.data : child.data.head);
            // Recursed for BOTH kinds — see "people have children" above.
            walk(child.id);
        }
    };
    walk(nodeId);
    return [...out.values()];
}
/** The ids alone, for a caller building command assignments rather than rendering a list. */
export function subtreePeopleIds(nodes, nodeId) {
    return subtreePeople(nodes, nodeId).map((p) => String(p.id));
}
/**
 * What a held selection means in the view the user is looking at.
 *
 * The held set survives every lens change — a lens must lose nothing, so clearing a filter has to bring
 * people back — and this is the function that decides what may be ACTED on: the resolved subset, never
 * the held one. The frozen app has no equivalent, and that is its worst behaviour in this area. Nothing
 * there prunes on a lens change and its bulk handlers close over a population resolved once when the
 * panel opened, so changing perspective left a live "→ No longer required" button bound to a stale set,
 * rewriting people who were no longer on screen, with no further interaction required.
 *
 * The two counts that are not `resolved.length` are the disclosure: a caller that does not print them is
 * hiding the gap rather than closing it.
 *
 * Two asymmetries are deliberate and both follow the frozen selection panel rather than the group panel:
 *
 *  · FTE and salary count `countsInHeadcount` only, while the headline and the bucket tally count
 *    everybody resolved. Each figure then sums to the number printed beside it. The GROUP panel filters
 *    its tally instead, because it sits under a headcount row that also filters — reusing that block
 *    here would print "Remain 5" under a header saying "10 people selected".
 *  · `undecided` is its own tally row rather than being read as Remain. The platform models the two as
 *    distinct — `currentBucket` reads with `hasOwn`, and an explicit Remain is deliberately storable —
 *    and this is the one place the port must NOT follow the app, whose panel reads undecided as Remain
 *    and whose bulk "→ Remain" then opts a whole team out of the perimeter rule with nothing said.
 */
export function selectionRollup(state, nodes, held) {
    // A group's or the root's head is drawn on that card rather than as a leaf, so both kinds contribute.
    const personOn = (node) => node.kind === 'person' ? node.data : node.data.head;
    const drawn = new Map();
    for (const node of nodes) {
        const person = personOn(node);
        if (person)
            drawn.set(String(person.id), person);
    }
    const onRoster = new Set();
    for (const person of state.employees)
        onRoster.add(String(person.id));
    for (const hire of state.design.buildUps)
        onRoster.add(String(hire.id));
    let hiddenByLens = 0;
    let stale = 0;
    for (const id of held) {
        if (drawn.has(id))
            continue;
        // On the roster but not in this view is a LENS gap and must be reversible; not on the roster at all
        // is gone, and gets pruned. The two are counted apart because only one of them is the user's doing.
        if (onRoster.has(id))
            hiddenByLens++;
        else
            stale++;
    }
    // Walked in tree order, so the member list reads like the chart rather than like the click history.
    // Deduplicated because a head is drawn on their own group's card and can be reached twice.
    const unique = [];
    const seen = new Set();
    for (const node of nodes) {
        const person = personOn(node);
        if (!person)
            continue;
        const id = String(person.id);
        if (!held.has(id) || seen.has(id))
            continue;
        seen.add(id);
        unique.push(person);
    }
    const bucketCounts = {
        Remain: 0,
        'Carve-out': 0,
        'No longer required': 0,
        undecided: 0,
    };
    let fte = 0;
    let salary = 0;
    let buildUps = 0;
    for (const person of unique) {
        if (person.isBuildUp)
            buildUps++;
        if (countsInHeadcount(person)) {
            fte += Number(person.fte) || 0;
            salary += fxSalary(person, state.assumptions);
        }
        /*
          Read with `hasOwn`, and NOT through `currentBucket`.
    
          `currentBucket` answers "where is this person" and defaults an absent row to Remain, which is right
          for the chart and wrong here: this tally's whole job is to show undecided as its own number, because
          "→ Remain" over an undecided person is a real state change that opts them out of the perimeter rule.
          `hasOwn` also keeps a person id that collides with an Object.prototype member out of the answer.
        */
        const id = String(person.id);
        const stored = hasOwn(state.design.buckets, id) ? state.design.buckets[id] : null;
        bucketCounts[stored ?? 'undecided'] += 1;
    }
    return {
        resolved: unique,
        hiddenByLens,
        stale,
        people: unique.length,
        buildUps,
        fte,
        salary,
        bucketCounts,
    };
}
/* ------------------------------------------------------------------ *
 * What a bulk may be aimed at
 * ------------------------------------------------------------------ */
/**
 * The ids a bulk field edit can be aimed at, and how many it cannot.
 *
 * `setEmpFieldBulk` refuses ANY planned hire in ANY perspective, and refuses the whole batch rather than
 * skipping — a hire has no imported record, and an override keyed to one is read by the panel while
 * every figure pricing the hire keeps reading the position. A cross-chart selection is heterogeneous by
 * construction and hires are deliberately selectable, so a mixed selection is the ordinary case. Naming
 * the excluded count is the P4i rule: a control must not offer what its subject cannot honour.
 */
export function fieldEditableIds(resolved) {
    const ids = [];
    let excludedBuildUps = 0;
    for (const person of resolved) {
        if (person.isBuildUp)
            excludedBuildUps++;
        else
            ids.push(String(person.id));
    }
    return { ids, excludedBuildUps };
}
/**
 * The ids a bucket bulk can be aimed at, per bucket, and how many it skips.
 *
 * Only one bucket excludes anybody: `assertBucketable` refuses a planned hire made No-longer-required,
 * because a position nobody has filled is deleted rather than made redundant. Every other bucket takes
 * the whole selection.
 */
export function bucketableIds(resolved, bucket) {
    if (bucket !== 'No longer required') {
        return { ids: resolved.map((p) => String(p.id)), skippedBuildUps: 0 };
    }
    const ids = [];
    let skippedBuildUps = 0;
    for (const person of resolved) {
        if (person.isBuildUp)
            skippedBuildUps++;
        else
            ids.push(String(person.id));
    }
    return { ids, skippedBuildUps };
}
/**
 * Whether a bulk of this size needs confirming, and the sentence to confirm with.
 *
 * The frozen app arms a field edit above 25 and a bucket assignment at no size at all. The threshold is
 * ported; the omission is not. `'No longer required'` is the most destructive action on the platform —
 * it drops the stage metadata and the stranded mitigation plan of everybody it touches — and the app
 * gates a job-title edit at 26 people while gating that at nothing. Recorded as a divergence.
 */
export const BULK_CONFIRM_ABOVE = 25;
export function bulkArmPolicy(op, bucket, n) {
    if (op === 'field' && n > BULK_CONFIRM_ABOVE) {
        return { confirm: true, reason: `Set this field for ${n} people?` };
    }
    if (op === 'bucket' && bucket === 'No longer required' && n > BULK_CONFIRM_ABOVE) {
        return {
            confirm: true,
            reason: `Make ${n} people no longer required? This also clears their transfer stage and any RemainCo ` +
                `mitigation plan they carry.`,
        };
    }
    return { confirm: false, reason: '' };
}
/**
 * Split a bucket bulk by whose decision the perimeter rule currently owns.
 *
 * `setBucketBulk` takes `src` per assignment precisely so this can be one command: a deliberate bulk IS
 * the decision, so it should stamp 'manual' — but doing that to somebody the rule owns removes them from
 * the slider's reach for good, and NOT doing it lets the next slider drag delete the bulk move outright.
 * Neither single answer is right for a mixed selection, so each person gets the answer that fits them:
 * 'manual' for a decision a human already owned, nothing for a rule pick, which carries its source
 * forward.
 *
 * The caller must say how many it is taking away from the slider. Doing this silently is the thing the
 * command's own documentation warns against at length.
 */
export function bucketAssignments(state, ids, bucket) {
    const assignments = [];
    let ruleOwned = 0;
    for (const employeeId of ids) {
        const meta = state.design.bucketMeta[employeeId];
        const owned = meta?.src === 'rule';
        if (owned)
            ruleOwned++;
        assignments.push({ employeeId, bucket, ...(owned ? {} : { src: 'manual' }) });
    }
    return { assignments, ruleOwned };
}
//# sourceMappingURL=selection.js.map